<template>
  <div v-if="selectedAddress">
    <span
      v-if="(unknownAddress || addressNotFoundError) && !choseToEnterManually"
      class="error-text text-danger"
    >
      <i class="fa fa-exclamation-circle" />
      The location of the address could not be identified. Please search again
      or use the map below to pinpoint the exact address location.
    </span>
    <span v-else-if="selectedAddress.pinned">
      <i class="fa fa-circle-info" />
      This address location has been manually pinned. Click the map below if
      this needs to be adjusted.
    </span>
    <div class="d-flex flex-row">
      <div class="flex-1 d-flex mr-3">
        <TextField
          v-model="address.street"
          name="street"
          label="Street"
          :validation-errors="v$.$errors"
          label-class="flex-1 mr-3"
        />
        <TextField
          v-model="address.city"
          name="city"
          label="City"
          label-class="flex-1"
          :validation-errors="v$.$errors"
        />
      </div>
      <div class="flex-1 d-flex">
        <Select
          v-model="address.state"
          name="State"
          search
          class="flex-1 mr-3 state-field"
          :options="stateOptions"
          :field-error="v$.address ? firstErrorMessage(v$.address.state) : ''"
          :validation-errors="v$.$errors"
        />
        <TextField
          v-model="address.zip_code"
          name="zip_code"
          label="Zip Code"
          label-class="flex-1"
          :validation-errors="v$.$errors"
        />
      </div>
    </div>
    <LocationSelector
      v-if="unknownAddress || selectedAddress.pinned || addressNotFoundError"
      :starting-address="selectedAddress"
      :show-info-window="unknownAddress"
      @update:location="pinAddress"
    />
  </div>
</template>

<script lang="ts">
import { STATE_OPTIONS } from "serviceshift-ui/shared/src/lib/stateOptions";
import mergeKnownProperties from "@/lib/mergeKnownProperties";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import TextField from "@/components/TextField.vue";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import LocationSelector from "../../components/LocationSelector.vue";

class AddressManualInputData {
  city = "";
  state = "";
  street = "";
  zip_code = "";
  longitude = null;
  latitude = null;
  pinned = false;
  manual_entry = false;
  constructor(params: Partial<AddressManualInputData> = {}) {
    // This will help us merge only the properties we care about
    mergeKnownProperties(this, params);
  }
}

export default {
  components: {
    LocationSelector,
    TextField
  },
  props: {
    selectedAddress: {
      type: Object,
      default: null
    },
    choseToEnterManually: {
      type: Boolean,
      default: false
    },
    typedAddress: {
      type: String,
      default: ""
    }
  },
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  validations() {
    return {
      address: {
        street: {
          required: helpers.withMessage("Street is required", required),
          $autoDirty: true
        },
        city: {
          required: helpers.withMessage("City is required", required),
          $autoDirty: true
        },
        state: {
          required: helpers.withMessage("State is required", required),
          $autoDirty: true
        },
        zip_code: {
          required: helpers.withMessage("Zip Code is required", required),
          $autoDirty: true
        }
      }
    };
  },
  data() {
    return {
      address: new AddressManualInputData(this.selectedAddress),
      stateOptions: STATE_OPTIONS,
      addressNotFoundError: false
    };
  },
  computed: {
    unknownAddress() {
      return !this.selectedAddress.id;
    }
  },
  watch: {
    selectedAddress: function (value) {
      this.address = new AddressManualInputData(value);
    },
    choseToEnterManually(value) {
      if (value) {
        this.address.manual_entry = true;
        if (this.typedAddress) {
          this.address.street = this.typedAddress;
        }
      } else {
        this.address.manual_entry = false;
      }
    }
  },
  methods: {
    firstErrorMessage,
    async validate() {
      return this.v$.$validate();
    },
    setAddressNotFoundError(hasError) {
      this.addressNotFoundError = hasError;
    },
    async getFormData() {
      const isValid = await this.v$.$validate();
      if (isValid) return this.address;
      throw new Error("Invalid form data");
    },
    pinAddress(location) {
      this.address = new AddressManualInputData({
        city: location.city,
        street: location.street,
        state: location.state,
        zip_code: location.zipCode,
        latitude: location.latitude,
        longitude: location.longitude,
        pinned: true
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.small-fields {
  width: 20%;
}
.normal-field {
  width: 27%;
}
.manual-address-field {
  margin-left: 2%;
}
</style>
